/**
 * Marco de la app autenticada: barra lateral (navegación, proyectos, uso) y
 * contenido. Grabar ocurre en la extensión; el botón explica cómo.
 */
import { useRef, useState } from 'react'
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router'
import { useApp, useToast, errorText } from './AppContext'
import { Brand, Modal, Skeleton, UsageMeter } from './ui'
import {
  IconAdmin,
  IconFolder,
  IconHelp,
  IconLibrary,
  IconPlan,
  IconPlus,
  IconSettings,
  IconTasks,
  IconTeam,
  IconUpload
} from './icons'
import { useAsync } from '../lib/hooks'
import { auth } from '../lib/auth'

export function RequireAuth() {
  const { session, ready } = useApp()
  const loc = useLocation()
  if (!ready) return null
  if (!session)
    return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />
  return <Outlet />
}

export function Layout() {
  const { api, usage, me, accountError, org, orgId, switchOrg, suspendida } = useApp()
  // En un equipo, las carpetas las crean dueño y administradores
  const manageFolders = org?.tipo !== 'equipo' || org.rol === 'owner' || org.rol === 'admin'
  const toast = useToast()
  const nav = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [howTo, setHowTo] = useState(false)
  const [newProject, setNewProject] = useState<string | null>(null)
  // Se recarga al saber la organización activa y al cambiarla
  const projects = useAsync(() => (orgId ? api.projects() : Promise.resolve([])), [api, orgId])
  // Escape cancela (el blur que sigue no debe crear) y Enter + blur no crean dos veces
  const projectDone = useRef(false)

  function startProject() {
    projectDone.current = false
    setNewProject('')
  }

  async function createProject() {
    if (projectDone.current) return
    projectDone.current = true
    const nombre = newProject?.trim()
    if (!nombre) return setNewProject(null)
    try {
      const p = await api.createProject(nombre)
      setNewProject(null)
      await projects.reload()
      nav(`/p/${p.id}`)
    } catch (e) {
      projectDone.current = false
      toast(errorText(e), 'error')
    }
  }

  if (suspendida) return <CuentaSuspendida />
  return (
    <div className="layout">
      <a className="skip" href="#contenido">
        Saltar al contenido
      </a>
      <div className="topbar">
        <Brand />
        <button className="btn sm" onClick={() => setMenuOpen((v) => !v)} aria-expanded={menuOpen}>
          Menú
        </button>
      </div>
      <aside
        className={`sidebar ${menuOpen ? 'open' : ''}`}
        onClick={(e) => (e.target as HTMLElement).closest('a') && setMenuOpen(false)}
      >
        <Brand />
        {(me?.orgs.length ?? 0) > 1 && (
          <select
            className="org-switch"
            aria-label="Organización"
            value={orgId ?? ''}
            onChange={(e) => switchOrg(e.target.value)}
          >
            {me!.orgs.map((o) => (
              <option key={o.orgId} value={o.orgId}>
                {o.tipo === 'personal' ? 'Personal' : o.nombre}
              </option>
            ))}
          </select>
        )}
        <button className="btn live block" onClick={() => setHowTo(true)}>
          <span className="rec-dot" aria-hidden="true" /> Grabar
        </button>
        <nav className="nav" aria-label="Principal">
          <NavLink to="/" end>
            <IconLibrary /> Biblioteca
          </NavLink>
          <NavLink to="/tareas">
            <IconTasks /> Tareas
          </NavLink>
          <NavLink to="/import">
            <IconUpload /> Importar audio
          </NavLink>
          <NavLink to="/equipo">
            <IconTeam /> Equipo
          </NavLink>
          <NavLink to="/plan">
            <IconPlan /> Plan y uso
          </NavLink>
          <NavLink to="/settings">
            <IconSettings /> Ajustes
          </NavLink>
          <NavLink to="/ayuda">
            <IconHelp /> Ayuda
          </NavLink>
          {me?.user.esAdmin && (
            <NavLink to="/admin">
              <IconAdmin /> Administración
            </NavLink>
          )}
        </nav>
        <div className="side-sec">Proyectos</div>
        <nav className="nav" aria-label="Proyectos">
          {(projects.data ?? []).map((p) => (
            <NavLink key={p.id} to={`/p/${p.id}`}>
              <IconFolder /> {p.nombre}
            </NavLink>
          ))}
          {!manageFolders ? null : newProject === null ? (
            <button onClick={startProject}>
              <IconPlus /> {org?.tipo === 'equipo' ? 'Nueva carpeta' : 'Nuevo proyecto'}
            </button>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                void createProject()
              }}
            >
              <input
                autoFocus
                aria-label="Nombre del proyecto"
                placeholder="Nombre del proyecto"
                value={newProject}
                maxLength={120}
                onChange={(e) => setNewProject(e.target.value)}
                onBlur={() => void createProject()}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') return
                  projectDone.current = true
                  setNewProject(null)
                }}
                style={{ width: '100%' }}
              />
            </form>
          )}
        </nav>
        <div className="side-foot">
          {usage && <UsageMeter usage={usage} compact />}
          <div className="who-am-i">
            <span className="avatar" aria-hidden="true">
              {initials(me?.user.nombre ?? me?.user.email ?? '')}
            </span>
            <span className="grow" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {me?.user.nombre ?? me?.user.email}
            </span>
            <button
              className="iconbtn"
              onClick={() => void auth.signOut().then(() => nav('/login'))}
              title="Cerrar sesión"
            >
              Salir
            </button>
          </div>
        </div>
      </aside>
      <main className="content" id="contenido" tabIndex={-1}>
        {me?.aviso && (
          <div className="notice accent" role="status" style={{ margin: '12px 24px 0' }}>
            {me.aviso}
          </div>
        )}
        {accountError && !me && !suspendida && (
          <div className="page">
            <div className="notice warn">{accountError}</div>
          </div>
        )}
        {/* Cambio explícito de organización: todas las vistas se vuelven a cargar (no al
            cargar /me la primera vez: eso duplicaría todas las peticiones) */}
        {/* Hasta saber la organización activa (llega con /me) no se pide nada: si no, la
            primera petición iría a la personal */}
        {me ? (
          <Outlet context={{ reloadProjects: projects.reload }} />
        ) : (
          !accountError && (
            <div className="page stack" aria-busy="true">
              <Skeleton h={34} w="40%" />
              <Skeleton h={220} />
            </div>
          )
        )}
      </main>
      <Modal
        open={howTo}
        onClose={() => setHowTo(false)}
        title="Grabar una reunión"
        footer={
          <button className="btn primary" onClick={() => setHowTo(false)}>
            Entendido
          </button>
        }
      >
        <p>eco graba desde la extensión del navegador, sin bots en la llamada.</p>
        <ol className="steps">
          <li>Entra a tu reunión de Google Meet en Chrome o Edge.</li>
          <li>
            Haz clic en el ícono de eco en la barra del navegador, o pulsa <kbd>Alt+Shift+R</kbd>.
          </li>
          <li>Marca «Avisé a los participantes» y pulsa Grabar. Al terminar, pulsa Detener.</li>
        </ol>
        <p className="muted">
          ¿Reunión presencial? En el mismo menú elige «Presencial (solo micrófono)».
        </p>
        <NavLink to="/bienvenida" onClick={() => setHowTo(false)}>
          ¿No tienes la extensión? Instálala aquí
        </NavLink>
      </Modal>
    </div>
  )
}

/** Iniciales para el avatar: «Carla Pérez» → «CP»; un correo → su primera letra. */
function initials(nombre: string): string {
  const partes = nombre
    .split('@')[0]!
    .split(/[\s._-]+/)
    .filter(Boolean)
  return ((partes[0]?.[0] ?? '') + (partes[1]?.[0] ?? '')).toUpperCase() || '·'
}

/**
 * Cuenta suspendida desde el panel: no entra a eco, pero puede descargar sus
 * datos y borrar la cuenta (derechos de acceso y supresión) o cerrar sesión.
 */
function CuentaSuspendida() {
  const { api } = useApp()
  const toast = useToast()
  const [borrar, setBorrar] = useState(false)
  const [texto, setTexto] = useState('')
  return (
    <div className="suspended-screen">
      <div className="card stack" style={{ maxWidth: 480 }}>
        <Brand />
        <h1 style={{ margin: 0, fontSize: 22 }}>Tu cuenta está suspendida</h1>
        <p className="muted" style={{ margin: 0 }}>
          No puedes usar eco por ahora. Si crees que es un error, escríbenos a{' '}
          <a className="link" href="mailto:hola@holaeco.cl">
            hola@holaeco.cl
          </a>
          . Igual puedes descargar tus datos o borrar tu cuenta.
        </p>
        <div className="row wrap" style={{ gap: 8 }}>
          <button
            className="btn"
            onClick={async () => {
              try {
                await api.exportData()
                toast('Te enviaremos un enlace por correo para descargar tus datos')
              } catch (e) {
                toast(errorText(e), 'error')
              }
            }}
          >
            Descargar mis datos
          </button>
          <button className="btn danger" onClick={() => setBorrar(true)}>
            Borrar mi cuenta
          </button>
          <button className="btn ghost" onClick={() => void auth.signOut()}>
            Salir
          </button>
        </div>
        {borrar && (
          <div className="stack" style={{ gap: 8 }}>
            <label className="field">
              <span>Escribe ELIMINAR para confirmar</span>
              <input value={texto} onChange={(e) => setTexto(e.target.value)} />
            </label>
            <div>
              <button
                className="btn danger"
                disabled={texto.trim() !== 'ELIMINAR'}
                onClick={async () => {
                  try {
                    await api.deleteAccount()
                    toast('Tu cuenta se está borrando')
                    await auth.signOut()
                  } catch (e) {
                    toast(errorText(e), 'error')
                  }
                }}
              >
                Borrar para siempre
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
